import { element, slot } from "./dom.js";
import type { SheetSentence } from "./sheet-sentences.js";

/**
 * The sentences a sentence sheet may print, each with a box to leave it out.
 *
 * Drawn into `data-cb="sentence-picker"` once a source's 例句 have arrived,
 * and drawn again only when they change, so ticking a box keeps the list
 * where the reader had scrolled it. What is left out is written into the
 * hidden `data-field="skip"`, by `skipKey`, and the change bubbles up to the
 * sheet's controls, which read it into the address with every other setting.
 * A kept link therefore prints the same sentences.
 */

/** How many characters of a sentence's id the address keeps to name it. */
const keyLength = 8;

/** What names a sentence in the address: the start of its id. */
export function skipKey(uuid: string): string {
  return uuid.slice(0, keyLength);
}

/** The keys of the sentences left out, as the address holds them. */
export function skipped(value: string): ReadonlySet<string> {
  return new Set(value.split(".").filter((key) => key !== ""));
}

/** The address's value for the sentences left out. */
export function skipValue(keys: Iterable<string>): string {
  return [...keys].sort().join(".");
}

/** The picker on a sentence sheet's page. */
export class SentencePicker {
  readonly #root: HTMLElement;
  readonly #list: HTMLElement;
  readonly #count: HTMLElement | undefined;
  readonly #skip: HTMLInputElement;
  #shown = "";

  private constructor(
    root: HTMLElement,
    list: HTMLElement,
    count: HTMLElement | undefined,
    skip: HTMLInputElement,
  ) {
    this.#root = root;
    this.#list = list;
    this.#count = count;
    this.#skip = skip;
    list.addEventListener("change", () => {
      this.#write();
    });
    for (const [name, checked] of [
      ["pick-all", true],
      ["pick-none", false],
    ] as const) {
      slot(root, name)?.addEventListener("click", () => {
        for (const box of this.#boxes()) {
          box.checked = checked;
        }
        this.#write();
        this.#skip.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
  }

  /** The picker on this sheet's controls, or nothing on a page without one. */
  static on(container: HTMLElement): SentencePicker | undefined {
    const root = slot(container, "sentence-picker");
    const list = root === undefined ? undefined : slot(root, "sentences");
    const skip = container.querySelector<HTMLInputElement>(
      'input[data-field="skip"]',
    );

    return root === undefined || list === undefined || skip === null
      ? undefined
      : new SentencePicker(root, list, slot(root, "picked"), skip);
  }

  /**
   * Show these sentences, ticked unless left out.
   * @param left - The keys of the sentences left out.
   */
  show(sentences: readonly SheetSentence[], left: ReadonlySet<string>): void {
    const shown = sentences.map((sentence) => sentence.uuid).join(" ");
    if (shown !== this.#shown) {
      this.#shown = shown;
      this.#list.replaceChildren(
        ...sentences.map((sentence) => this.#row(sentence)),
      );
    }
    for (const box of this.#boxes()) {
      box.checked = !left.has(box.value);
    }
    this.#root.hidden = sentences.length === 0;
    this.#counted();
  }

  /** Hide the picker, for a source with no sentences to choose among. */
  hide(): void {
    this.#root.hidden = true;
  }

  #row(sentence: SheetSentence): HTMLElement {
    const box = element("input", {
      attributes: { type: "checkbox", value: skipKey(sentence.uuid) },
    });

    return element("label", {
      className: "cb-ws-pick",
      children: [
        box,
        element("span", {
          className: "cb-ws-pick-text",
          children: [
            element("span", {
              className: "hanzi",
              text: sentence.text,
              attributes: { lang: "zh-Hans" },
            }),
            element("span", {
              className: "cb-ws-pick-english",
              text: sentence.english,
            }),
          ],
        }),
      ],
    });
  }

  #boxes(): readonly HTMLInputElement[] {
    return [
      ...this.#list.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]',
      ),
    ];
  }

  /**
   * Write what is left out of the sentences shown. A sentence of another
   * source, or one a shorter length hides, is forgotten, so the address names
   * only what this sheet leaves out.
   */
  #write(): void {
    this.#skip.value = skipValue(
      this.#boxes()
        .filter((box) => !box.checked)
        .map((box) => box.value),
    );
    this.#counted();
  }

  #counted(): void {
    if (this.#count === undefined) {
      return;
    }
    const boxes = this.#boxes();
    const ticked = boxes.filter((box) => box.checked).length;
    this.#count.textContent = `${String(ticked)} of ${String(boxes.length)} sentences chosen`;
  }
}
